import { FC, useState, useEffect, useMemo, FormEvent } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { withPage } from "../../components/Page";
import {
  structuredSearchCatalog,
  SearchResultItem,
} from "../../search/searchData";
import styles from "./Search.module.scss";

// Declare pagefind on window
declare global {
  interface Window {
    pagefind?: {
      search: (query: string) => Promise<{
        results: Array<{
          id: string;
          score: number;
          data: () => Promise<{
            url: string;
            meta: { title: string; image?: string };
            excerpt: string;
            content: string;
          }>;
        }>;
      }>;
    };
  }
}

const Search: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = searchParams.get("q") || "";
  const [inputQuery, setInputQuery] = useState(query);
  const [selectedTab, setSelectedTab] = useState<
    "all" | "pages" | "documents" | "drawings" | "assets"
  >("all");
  const [selectedDiscipline, setSelectedDiscipline] = useState<string | null>(
    null,
  );
  const [pagefindResults, setPagefindResults] = useState<SearchResultItem[]>(
    [],
  );
  const [isSearchingPagefind, setIsSearchingPagefind] = useState(false);

  // Load Pagefind script if available and search
  useEffect(() => {
    let isMounted = true;
    const runSearch = async () => {
      if (!query.trim()) {
        if (isMounted) setPagefindResults([]);
        return;
      }

      setIsSearchingPagefind(true);
      try {
        let pf = window.pagefind;
        if (!pf) {
          try {
            // eslint-disable-next-line no-eval
            pf = await eval('import("/pagefind/pagefind.js")');
            window.pagefind = pf;
          } catch (e) {
            // fallback if relative import fails
          }
        }
        if (pf) {
          const searchOutput = await pf.search(query);
          const detailedResults = await Promise.all(
            searchOutput.results.map(async (r) => {
              const d = (await r.data()) as any;
              // Extract discipline from pagefind filters if present
              const disciplineFilter = d.filters?.discipline;
              const discipline = Array.isArray(disciplineFilter)
                ? disciplineFilter[0]
                : typeof disciplineFilter === "string"
                  ? disciplineFilter
                  : "Building";

              // Ensure correct URL mapping back to actual React routes
              let targetUrl =
                d.meta?.url ||
                d.url.replace(".html", "").replace("/pages/", "/");
              if (targetUrl === "/overview") targetUrl = "/";

              return {
                id: r.id,
                title: d.meta?.title || "Pulse Page",
                type: "page" as const,
                category: "Pulse Page",
                discipline: discipline,
                url: targetUrl,
                content: d.excerpt || d.content,
                meta: { published: "12 Sep 2026", section: "Indexed content" },
              };
            }),
          );
          if (isMounted) {
            setPagefindResults(detailedResults);
          }
        }
      } catch (err) {
        console.error("Pagefind search error:", err);
      } finally {
        if (isMounted) setIsSearchingPagefind(false);
      }
    };

    runSearch();
    return () => {
      isMounted = false;
    };
  }, [query]);

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (inputQuery.trim()) {
      setSearchParams({ q: inputQuery.trim() });
    } else {
      setSearchParams({});
    }
  };

  const combinedResults = useMemo(() => {
    const q = query.toLowerCase().trim();

    // Filter structured catalog (documents, drawings, assets)
    const matchedCatalog = structuredSearchCatalog.filter((item) => {
      if (!q) return true;
      return (
        item.title.toLowerCase().includes(q) ||
        item.content.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
      );
    });

    // Combine pagefind page results + structured catalog results
    const all = [...pagefindResults, ...matchedCatalog];

    return all.filter((item) => {
      if (selectedTab === "pages" && item.type !== "page") return false;
      if (selectedTab === "documents" && item.type !== "document") return false;
      if (selectedTab === "drawings" && item.type !== "drawing") return false;
      if (selectedTab === "assets" && item.type !== "asset") return false;

      if (selectedDiscipline && item.discipline !== selectedDiscipline)
        return false;
      return true;
    });
  }, [query, pagefindResults, selectedTab, selectedDiscipline]);

  const highlightMatch = (text: string, q: string) => {
    if (!q.trim()) return text;
    // If text already contains html tags from pagefind excerpt, render as html or highlight plain text
    if (text.includes("<") && text.includes(">")) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }
    // Escape special regex characters in query to prevent invalid regular expression syntax errors
    const escapedQ = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escapedQ})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase() ? (
        <mark key={i} className={styles.highlight}>
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  const counts = useMemo(() => {
    const q = query.toLowerCase().trim();
    const matchedCatalog = structuredSearchCatalog.filter((item) => {
      if (!q) return true;
      return (
        item.title.toLowerCase().includes(q) ||
        item.content.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
      );
    });
    const allBase = [...pagefindResults, ...matchedCatalog];

    return {
      all: allBase.length,
      pages: allBase.filter((i) => i.type === "page").length,
      documents: allBase.filter((i) => i.type === "document").length,
      drawings: allBase.filter((i) => i.type === "drawing").length,
      assets: allBase.filter((i) => i.type === "asset").length,
      mechanical: allBase.filter((i) => i.discipline === "Mechanical").length,
      electrical: allBase.filter((i) => i.discipline === "Electrical").length,
      building: allBase.filter((i) => i.discipline === "Building").length,
    };
  }, [query, pagefindResults]);

  return (
    <main className={styles.container} tabIndex={-1}>
      <div className={styles.searchHeaderArea}>
        <div className={styles.eyebrow}>Commerzbank · Building Information</div>
        <h1 className={styles.pageTitle}>Search Pulse</h1>
        <p className={styles.pageSubtitle}>
          Find pages, assets, drawings and supporting documents indexed via
          Pagefind.
        </p>

        <form
          className={styles.searchForm}
          role="search"
          onSubmit={handleSearchSubmit}
        >
          <div className={styles.searchBoxWrapper}>
            <span className={styles.searchIcon} aria-hidden="true">
              ⌕
            </span>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search building information, e.g. chilled water, Hi-Fog, ventilation..."
              value={inputQuery}
              onChange={(e) => setInputQuery(e.target.value)}
              aria-label="Search building information"
            />
            <button type="submit" className={styles.searchSubmitBtn}>
              Search
            </button>
          </div>
        </form>
        <div className={styles.searchTip}>
          Powered by Pagefind (build-time static indexing) + structured document
          metadata.
        </div>
      </div>

      <div className={styles.resultsCountBar}>
        <h2>
          {combinedResults.length}{" "}
          {combinedResults.length === 1 ? "result" : "results"}
          {query ? ` for “${query}”` : ""}
          {isSearchingPagefind && (
            <span className={styles.loadingNote}> (Searching index...)</span>
          )}
        </h2>
        <span className={styles.scopeNotice}>
          Search scope: Pagefind Index + Structured Catalog
        </span>
      </div>

      <div className={styles.filterTabsBar}>
        <button
          type="button"
          className={selectedTab === "all" ? styles.activeTab : ""}
          onClick={() => setSelectedTab("all")}
        >
          All {counts.all}
        </button>
        <button
          type="button"
          className={selectedTab === "pages" ? styles.activeTab : ""}
          onClick={() => setSelectedTab("pages")}
        >
          Pages {counts.pages}
        </button>
        <button
          type="button"
          className={selectedTab === "documents" ? styles.activeTab : ""}
          onClick={() => setSelectedTab("documents")}
        >
          Documents {counts.documents}
        </button>
        <button
          type="button"
          className={selectedTab === "drawings" ? styles.activeTab : ""}
          onClick={() => setSelectedTab("drawings")}
        >
          Drawings {counts.drawings}
        </button>
        <button
          type="button"
          className={selectedTab === "assets" ? styles.activeTab : ""}
          onClick={() => setSelectedTab("assets")}
        >
          Assets {counts.assets}
        </button>
      </div>

      <div className={styles.mainLayout}>
        <aside className={styles.refineSidebar}>
          <div className={styles.sidebarSection}>
            <h3>Refine results</h3>
            <div className={styles.facetGroup}>
              <span className={styles.facetTitle}>Content type</span>
              <label>
                <input
                  type="radio"
                  name="contentType"
                  checked={selectedTab === "all"}
                  onChange={() => setSelectedTab("all")}
                />
                All content ({counts.all})
              </label>
              <label>
                <input
                  type="radio"
                  name="contentType"
                  checked={selectedTab === "pages"}
                  onChange={() => setSelectedTab("pages")}
                />
                Pages ({counts.pages})
              </label>
              <label>
                <input
                  type="radio"
                  name="contentType"
                  checked={selectedTab === "documents"}
                  onChange={() => setSelectedTab("documents")}
                />
                Documents ({counts.documents})
              </label>
              <label>
                <input
                  type="radio"
                  name="contentType"
                  checked={selectedTab === "drawings"}
                  onChange={() => setSelectedTab("drawings")}
                />
                Drawings ({counts.drawings})
              </label>
              <label>
                <input
                  type="radio"
                  name="contentType"
                  checked={selectedTab === "assets"}
                  onChange={() => setSelectedTab("assets")}
                />
                Assets ({counts.assets})
              </label>
            </div>

            <div className={styles.facetGroup}>
              <span className={styles.facetTitle}>Discipline</span>
              <label>
                <input
                  type="checkbox"
                  checked={selectedDiscipline === "Mechanical"}
                  onChange={() =>
                    setSelectedDiscipline(
                      selectedDiscipline === "Mechanical" ? null : "Mechanical",
                    )
                  }
                />
                Mechanical ({counts.mechanical})
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={selectedDiscipline === "Electrical"}
                  onChange={() =>
                    setSelectedDiscipline(
                      selectedDiscipline === "Electrical" ? null : "Electrical",
                    )
                  }
                />
                Electrical ({counts.electrical})
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={selectedDiscipline === "Building"}
                  onChange={() =>
                    setSelectedDiscipline(
                      selectedDiscipline === "Building" ? null : "Building",
                    )
                  }
                />
                Building ({counts.building})
              </label>
              {(selectedDiscipline || selectedTab !== "all") && (
                <button
                  type="button"
                  className={styles.clearFiltersBtn}
                  onClick={() => {
                    setSelectedTab("all");
                    setSelectedDiscipline(null);
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </aside>

        <div className={styles.resultsList}>
          {combinedResults.length === 0 ? (
            <div className={styles.noResultsCard}>
              <h3>No matching building information found</h3>
              <p>
                Try searching for terms like "chilled water", "Hi-Fog",
                "ventilation", or "AHU-003".
              </p>
            </div>
          ) : (
            combinedResults.map((item: SearchResultItem, idx: number) => {
              const badgeClass =
                item.type === "page"
                  ? styles.badgePage
                  : item.type === "document"
                    ? styles.badgeDoc
                    : item.type === "drawing"
                      ? styles.badgeDwg
                      : styles.badgeAsset;

              const typeLabel =
                item.type === "page"
                  ? "PAGE"
                  : item.type === "document"
                    ? "PDF"
                    : item.type === "drawing"
                      ? "DWG"
                      : "ASSET";

              return (
                <div
                  key={`${item.id}-${idx}`}
                  className={`${styles.card} ${styles.resultCard}`}
                >
                  <div className={styles.resultMetaHeader}>
                    <span className={`${styles.typeBadge} ${badgeClass}`}>
                      {typeLabel}
                    </span>
                    <span className={styles.contextCrumb}>
                      {item.category} / {item.discipline}
                    </span>
                  </div>

                  <h3
                    className={styles.resultTitle}
                    onClick={() => {
                      if (item.type === "document") {
                        window.open(item.url, "_blank");
                      } else {
                        navigate(item.url);
                      }
                    }}
                  >
                    {highlightMatch(item.title, query)}
                  </h3>

                  <p className={styles.resultSnippet}>
                    {highlightMatch(item.content, query)}
                  </p>

                  <div className={styles.resultFooter}>
                    <div className={styles.footerFacts}>
                      {item.meta?.source && (
                        <span>
                          <b>Source:</b> {item.meta.source}
                        </span>
                      )}
                      {item.meta?.revision && (
                        <span>
                          <b>Revision:</b> {item.meta.revision}
                        </span>
                      )}
                      {item.meta?.published && (
                        <span>
                          <b>Published:</b> {item.meta.published}
                        </span>
                      )}
                      {item.meta?.manufacturer && (
                        <span>
                          <b>Manufacturer:</b> {item.meta.manufacturer}
                        </span>
                      )}
                      {item.meta?.location && (
                        <span>
                          <b>Location:</b> {item.meta.location}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.openTargetBtn}
                      onClick={() => {
                        if (item.type === "document") {
                          window.open(item.url, "_blank");
                        } else {
                          navigate(item.url);
                        }
                      }}
                    >
                      Open →
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </main>
  );
};

export default withPage(Search)({
  meta: {
    title: "Search Building Information · Pulse",
    description:
      "Search across Pulse pages, PDFs, documents and technical assets.",
  },
  breadcrumbs: [{ label: "Pulse", href: "/" }, { label: "Search" }],
});
